/** Sesión del admin: Google y claim `admin` (lo pone admin-grantRole). Sin claim, no se entra. */
import { useEffect, useState } from 'react';
import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithCredential,
  signInWithPopup,
  signOut,
  type User,
} from 'firebase/auth';
import { auth } from './firebase.js';

export interface AdminSession {
  user: User;
  isAdmin: boolean;
}

export function useAdminSession(): AdminSession | null | undefined {
  const [s, setS] = useState<AdminSession | null | undefined>(undefined);
  useEffect(() => {
    return onAuthStateChanged(auth, (user) => {
      if (!user) {
        setS(null);
        return;
      }
      user
        .getIdTokenResult(true)
        .then((r) => {
          setS({ user, isAdmin: r.claims.admin === true });
        })
        .catch(() => {
          setS({ user, isAdmin: false });
        });
    });
  }, []);
  return s;
}

/**
 * App Android «nuvvu Admin» (apps/admin-android): el panel va dentro de un WebView, donde Google no
 * deja abrir su ventana de acceso. La app hace el «Entrar con Google» nativo y nos pasa el idToken
 * con `window.nuvvuNativeSignIn(idToken)`; `NuvvuAdminNative` es el puente que la app inyecta.
 */
interface NativeBridge {
  signIn: () => void;
  signOut?: () => void;
}
declare global {
  interface Window {
    NuvvuAdminNative?: NativeBridge;
    nuvvuNativeSignIn?: (idToken: string) => Promise<boolean>;
  }
}
window.nuvvuNativeSignIn = async (idToken: string): Promise<boolean> => {
  try {
    await signInWithCredential(auth, GoogleAuthProvider.credential(idToken));
    return true;
  } catch {
    return false;
  }
};

export async function signIn(): Promise<void> {
  if (window.NuvvuAdminNative) {
    window.NuvvuAdminNative.signIn();
    return;
  }
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });
  await signInWithPopup(auth, provider);
}

export const logOut = (): Promise<void> => {
  window.NuvvuAdminNative?.signOut?.();
  return signOut(auth);
};
